/**
 * Route-specific LCP image preloads.
 *
 * client/index.html preloads the homepage hero portrait on every route. For a route
 * listed here the server swaps that preload for the route's own LCP image, so the
 * browser fetches the right image first instead of a homepage photo the page never shows.
 * The page component reads the same object so the <img> and the preload always agree
 * (same srcset + sizes = the same candidate is chosen, no double download).
 */
export type RouteImagePreload = {
  src: string;
  srcSet: string;
  sizes: string;
  width: number;
  height: number;
};

/**
 * XERF device image (manufacturer asset: the Cynosure Lutronic XERF tower). It was the page's hero
 * until 2026-10-09; it now sits in the "How it works" section, lazy-loaded, and the hero is the
 * manufacturer treatment demonstration loop (XERF_DEMO_VIDEO below). History: was hot-linked from cynosure.com as a 242 KB PNG (mobile LCP 11.6 s in
 * the 2026-10-08 ad-destination audit: a cross-origin connection plus the homepage portrait
 * preload competing for bandwidth). Now self-hosted as alpha webp at quality 82. The
 * manufacturer original is 550 px wide, so 550w is the native ceiling; larger candidates
 * would be upscales with no extra detail. Every candidate is under 60 KB.
 * Same srcset + sizes as the <img> in XerfSkinTightening.tsx.
 */
export const XERF_DEVICE_IMAGE: RouteImagePreload = {
  src: "/images/xerf-device-550.webp",
  srcSet: "/images/xerf-device-360.webp 360w, /images/xerf-device-480.webp 480w, /images/xerf-device-550.webp 550w",
  sizes: "(min-width: 700px) 240px, 210px",
  width: 550,
  height: 1309,
};

/**
 * Hero (v9): the first frame of the hero video (Dr. Kohli treating the chin of the consented
 * denim / brown-tank patient), 4:5. It is the poster and the LCP image; the muted loop only
 * starts after window load (client/src/pages/lp/facialBalancing/HeroVideo.tsx), so the video
 * never competes with it. Every candidate is under 120 KB. Same srcset + sizes as the <img> there.
 */
export const FACIAL_BALANCING_HERO_IMAGE: RouteImagePreload = {
  src: "/images/facial-balancing-hero-poster-720.webp",
  srcSet:
    "/images/facial-balancing-hero-poster-480.webp 480w, /images/facial-balancing-hero-poster-720.webp 720w, /images/facial-balancing-hero-poster-960.webp 960w",
  sizes: "(min-width: 900px) 460px, calc(100vw - 40px)",
  width: 960,
  height: 1200,
};

/**
 * CO2 ad LP hero poster (the Meta + Google CO2 campaigns' destination): the first frame of the
 * real-treatment loop (the same patient as the live Meta CO2 ad, owner-approved: black goggles, CO2
 * handpiece, red aiming beam), 4:5. It replaced a generic stock model on 2026-10-09 (owner:
 * "you need a real pic of my CO2 laser machine"). It is the LCP image; the muted loop only
 * mounts after window load (client/src/pages/lp/co2Laser/HeroVideo.tsx), so the video never
 * competes with it. Every candidate is under 120 KB. Same srcset + sizes as the <img> there.
 */
export const CO2_LP_HERO_IMAGE: RouteImagePreload = {
  src: "/images/co2-lp-hero-katie-720.webp",
  srcSet: "/images/co2-lp-hero-katie-480.webp 480w, /images/co2-lp-hero-katie-720.webp 720w",
  sizes: "(min-width: 900px) 460px, calc(100vw - 40px)",
  width: 720,
  height: 900,
};

/** A muted hero loop: its LCP poster plus WebM (VP9) and MP4 (H.264) sources, no audio track. */
export type HeroLoopVideo = {
  poster: RouteImagePreload;
  mp4: string;
  webm: string;
  width: number;
  height: number;
};

/**
 * The CO2 hero loop (owner 2026-10-09: "needs Katie Kohli laser tech doing treatment on patient"): 5.8 s cut
 * from the clinic Instagram reel DXz6l0Iu8a8 — wide shot of Katie treating at the CO2 machine, top-down
 * treatment, red-beam close-up. Cropped above the reel's burned-in captions; muted, no audio track.
 */
export const CO2_LP_HERO_VIDEO: HeroLoopVideo = {
  poster: CO2_LP_HERO_IMAGE,
  mp4: "/videos/co2-lp-hero-katie.mp4",
  webm: "/videos/co2-lp-hero-katie.webm",
  width: 720,
  height: 900,
};

/**
 * XERF page hero poster (/xerf-skin-tightening: indexed, and the Google + Meta XERF ad destination).
 * First frame of the official Cynosure Lutronic XERF treatment demonstration (manufacturer footage,
 * no Lakewood patient; the same clip the page used to show lower down, served from manus-storage),
 * self-hosted, 9:16 native 540x960. The hero frame crops it with object-fit (1:1 on phones, 4:5 from
 * 900px). 2026-10-09: replaced a real-patient clinic loop the same day; owner privacy call, that
 * patient is not to be on the website. Same srcset + sizes as the poster <img> in
 * client/src/pages/lp/co2Laser/HeroVideo.tsx.
 */
export const XERF_DEMO_POSTER: RouteImagePreload = {
  src: "/images/xerf-demo-poster-540.webp",
  srcSet: "/images/xerf-demo-poster-360.webp 360w, /images/xerf-demo-poster-540.webp 540w",
  sizes: "(min-width: 900px) 460px, min(calc(100vw - 40px), 300px)",
  width: 540,
  height: 960,
};

/** The XERF hero loop: the 15 s manufacturer demonstration, muted, no audio track. */
export const XERF_DEMO_VIDEO: HeroLoopVideo = {
  poster: XERF_DEMO_POSTER,
  mp4: "/videos/xerf-demo-loop.mp4",
  webm: "/videos/xerf-demo-loop.webm",
  width: 540,
  height: 960,
};

/**
 * RF microneedling ad LP hero (/lp/rf-microneedling-dallas, server-rendered static HTML in
 * server/microneedlingOfferLandingPages.ts, which writes its own preload tag). The RF segment of the
 * reel behind the live Meta RF ads (lakewood_30s_reel_FINAL, 13.0-15.8 s: the RF microneedling
 * handpiece on her cheek), cropped clear of the reel's burned-in captions and logo, 4:5.
 */
export const RF_LP_HERO_IMAGE: RouteImagePreload = {
  src: "/images/rf-lp-hero-poster-720.webp",
  srcSet: "/images/rf-lp-hero-poster-480.webp 480w, /images/rf-lp-hero-poster-720.webp 720w",
  sizes: "(min-width: 900px) 460px, calc(100vw - 40px)",
  width: 720,
  height: 900,
};

/** The RF hero loop: 5.5 s ping-pong, muted, no audio track. */
export const RF_LP_HERO_VIDEO: HeroLoopVideo = {
  poster: RF_LP_HERO_IMAGE,
  mp4: "/videos/rf-lp-hero-treatment.mp4",
  webm: "/videos/rf-lp-hero-treatment.webm",
  width: 720,
  height: 900,
};

/** /lp/best-med-spa-dallas hero mosaic, first tile (real patient, clinic Instagram). Same src as the page's eager <img>. */
export const BEST_MED_SPA_HERO_IMAGE: RouteImagePreload = {
  src: "/images/bm-hero-blonde-400.webp",
  srcSet: "/images/bm-hero-blonde-400.webp 400w",
  sizes: "(min-width: 900px) 230px, 50vw",
  width: 400,
  height: 500,
};

export const ROUTE_IMAGE_PRELOADS: Readonly<Record<string, RouteImagePreload>> = {
  "/xerf-skin-tightening": XERF_DEMO_POSTER,
  "/lp/co2-laser-dallas": CO2_LP_HERO_IMAGE,
  "/lp/best-med-spa-dallas": BEST_MED_SPA_HERO_IMAGE,
  "/lp/facial-balancing-dallas": FACIAL_BALANCING_HERO_IMAGE,
  "/lp/liquid-facelift-dallas": FACIAL_BALANCING_HERO_IMAGE,
};

export function getRouteImagePreload(pathname: string | undefined): RouteImagePreload | undefined {
  if (!pathname) return undefined;
  const clean = (pathname.split(/[?#]/)[0] || "/").replace(/\/+$/, "") || "/";
  return ROUTE_IMAGE_PRELOADS[clean];
}

function escapeAttr(value: string): string {
  return value.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

export function buildImagePreloadTag(image: RouteImagePreload): string {
  return `<link rel="preload" as="image" href="${escapeAttr(image.src)}" imagesrcset="${escapeAttr(image.srcSet)}" imagesizes="${escapeAttr(image.sizes)}" type="image/webp" fetchpriority="high" data-route-lcp-preload />`;
}

const HOMEPAGE_HERO_PRELOAD = /<link rel="preload" href="[^"]*dr_kohli_portrait_hero_[^"]*" as="image"[^>]*\/?>/;

/** Replace the template's homepage hero preload with the route's own LCP preload (no-op for unlisted routes). */
export function applyRouteImagePreload(template: string, pathname: string | undefined): string {
  const image = getRouteImagePreload(pathname);
  if (!image) return template;
  const tag = buildImagePreloadTag(image);
  if (HOMEPAGE_HERO_PRELOAD.test(template)) return template.replace(HOMEPAGE_HERO_PRELOAD, () => tag);
  return template.replace("</head>", () => `${tag}\n</head>`);
}
